import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { authApi, maybeRefreshToken } from '@/api/auth';
import { registerPushIfGranted } from '@/lib/push';
import { permissionsOnboardingDone } from '@/lib/permissions';
import { useAuthStore } from '@/store/auth';

export function RequireAuth({ children }: { children: ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const location = useLocation();

  // No mount: (1) sessão deslizante — renova o JWT se estiver na metade
  // final da janela de 30d (silencioso, one-shot, sem retry); (2) refresh do
  // /me — evita agir em cache stale (ex: bundle antigo salvou user sem
  // profile_complete, novo bundle lê undefined e faria redirect falso).
  useEffect(() => {
    if (!token) return;
    maybeRefreshToken()
      .then(() => authApi.me())
      .then((fresh) => setUser(fresh))
      .catch(() => {
        /* token inválido ou rede — deixa o fluxo normal tratar */
      });
    // App nativo: registra o token de push agora que há sessão (no-op no web,
    // idempotente). SEM prompt — o pedido de permissão mora no onboarding
    // (/onboarding/permissoes); aqui só registra quem já concedeu.
    void registerPushIfGranted();
  }, [token, setUser]);

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Só redireciona se o backend afirmou EXPLICITAMENTE profile_complete=false.
  // Campo undefined (cache antigo antes do deploy Google Sign-In) passa — o
  // useEffect acima vai refresh e, se necessário, o próximo render
  // redireciona. Evita loop inicial em users criados pré-deploy.
  if (
    user &&
    user.profile_complete === false &&
    location.pathname !== '/complete-profile'
  ) {
    // Carrega o destino junto — sem isso, quem chegou por deep link (ex.: QR
    // do aspirador) completava o perfil e caía na Home em vez de voltar.
    return <Navigate to="/complete-profile" state={{ from: location }} replace />;
  }

  // Onboarding único de permissões (app nativo, uma vez por instalação).
  // SÓ intercepta a Home: deep-links de QR (/qr, /configure) nunca são
  // sequestrados no meio do fluxo de recarga — todo mundo passa pela Home
  // eventualmente. Web/PWA não muda (isNativePlatform false).
  if (
    Capacitor.isNativePlatform() &&
    !permissionsOnboardingDone() &&
    location.pathname === '/'
  ) {
    return <Navigate to="/onboarding/permissoes" replace />;
  }

  return <>{children}</>;
}
